import { formatDate } from '../../dateFormat';
import { PattenUniversityLogo } from './PattenUniversityLogo';
import { useGeoPicker } from '../../geo-picker/picker';
import {
  TranscriptProgramEnrollment,
  TranscriptUser,
} from './TranscriptView';

interface Props {
  user: TranscriptUser;
  programEnrollments: TranscriptProgramEnrollment[];
  headingText: string;
  dateLabel: string;
}

const getProgramStatusLabel = (
  enrollment: TranscriptProgramEnrollment
): string => {
  if (enrollment.status === 'GRADUATED') {
    return enrollment.graduatedAt
      ? `Graduated ${formatDate(enrollment.graduatedAt as string)}`
      : 'Graduated';
  }
  return `Ongoing as of ${formatDate(new Date())}`;
};

const formatShortDate = (value: unknown): string | null => {
  if (!value) return null;
  return formatDate(value as string) || null;
};

// 30/40/30 horizontal layout on lg+ and print; vertical stack on mobile.
// shrink-0 grow-0 locks each column to its declared percentage.
const COLUMN_BASE =
  'flex flex-col min-w-0 lg:shrink-0 lg:grow-0 print:shrink-0 print:grow-0';
const LEFT_COLUMN = `${COLUMN_BASE} items-center lg:items-start lg:w-[30%] print:items-start print:w-[30%]`;
const CENTER_COLUMN = `${COLUMN_BASE} items-center gap-2 lg:w-[40%] print:w-[40%]`;
const RIGHT_COLUMN = `${COLUMN_BASE} items-center gap-1 break-words lg:items-end lg:self-start lg:w-[30%] print:items-end print:self-start print:w-[30%]`;

const TranscriptHeader = ({
  user,
  programEnrollments,
  headingText,
  dateLabel,
}: Props) => {
  const dob = formatShortDate(user.birthDate);
  const { getCountryByCode } = useGeoPicker();
  const address = user.countryOfOrigin
    ? getCountryByCode(user.countryOfOrigin)?.label ?? user.countryOfOrigin
    : null;

  return (
    <div className="flex flex-col items-center gap-6 w-full lg:flex-row lg:items-center lg:gap-0 print:flex-row print:items-start print:gap-0">
      <div className={LEFT_COLUMN}>
        <PattenUniversityLogo className="h-20 md:h-28 print:h-32" />
      </div>

      <div className={CENTER_COLUMN}>
        <div className="flex flex-col items-center w-full text-center">
          <h2 className="text-xl md:text-3xl font-semibold">{headingText}</h2>
          {programEnrollments.map((pe, i) => (
            <div
              key={i}
              className="text-sm md:text-base text-muted-foreground"
            >
              <p className="font-medium">{pe.program.name}</p>
              <p className="text-xs">{getProgramStatusLabel(pe)}</p>
            </div>
          ))}
        </div>
        <p className="text-xs md:text-sm text-muted-foreground text-center">
          2433 Coolidge Ave Oakland, CA 94601
          <br />
          (626) 966–4576
        </p>
        <div className="hidden print:flex print:flex-col print:items-center print:gap-1.5 print:w-3/4">
          <div className="w-full border-t border-muted" />
          <p className="text-xs text-muted-foreground text-center">
            {dateLabel}: {formatDate(new Date())}
          </p>
        </div>
      </div>

      <div className={RIGHT_COLUMN}>
        <p className="text-lg md:text-2xl font-semibold text-foreground print:text-lg break-words text-center lg:text-right print:text-right">
          {user.firstName} {user.lastName}
        </p>
        <div className="text-xs md:text-sm text-center w-full lg:text-right print:text-right">
          <p className="break-words">
            <span className="text-muted-foreground">Email: </span>
            <span className="text-foreground">{user.primaryEmail}</span>
          </p>
          {user.sandId && (
            <p className="break-all">
              <span className="text-muted-foreground">Student ID: </span>
              <span className="text-foreground">{user.sandId}</span>
            </p>
          )}
          {dob && (
            <p>
              <span className="text-muted-foreground">D.O.B.: </span>
              <span className="text-foreground">{dob}</span>
            </p>
          )}
          <p>
            <span className="text-muted-foreground">Address: </span>
            <span className="text-foreground">{address ?? '—'}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default TranscriptHeader;
